import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, BarChart3, TrendingUp, Euro, Zap, Sun, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { fetchTariffOverrides, resolvePriceForMonth, type TariffOverride } from '../utils/tariffOverrides';
import { loadParticipantVolumeSplit, type MonthSplit } from '../utils/csvDataLoader';

const normalizeFileName = (name: string): string => {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

interface ParticipantReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: any;
  initialYear?: string;
}

const DEFAULT_REFERENCE_TARIFF = 160;
const SHARED_ENERGY_PRICE = 80;
const NETWORK_SAVINGS_RATE = 21;

interface IndexationValues {
  [month: string]: number;
}

export function ParticipantReportModal({ isOpen, onClose, participant, initialYear }: ParticipantReportModalProps) {
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(initialYear || new Date().getFullYear().toString());
  const [exporting, setExporting] = useState(false);
  const [indexationValues, setIndexationValues] = useState<IndexationValues>({});
  const [sharedEnergyIdxValues, setSharedEnergyIdxValues] = useState<IndexationValues>({});
  const [tariffOverrides, setTariffOverrides] = useState<TariffOverride[]>([]);
  const [volumeSplits, setVolumeSplits] = useState<Record<string, MonthSplit>>({});

  useEffect(() => {
    if (isOpen && participant) {
      loadReportData();
    }
  }, [isOpen, participant, selectedYear]);

  const loadIndexationValues = async (paramName: string): Promise<IndexationValues> => {
    try {
      const { data, error } = await supabase
        .from('indexation_parameters')
        .select('month_key, value')
        .eq('param_name', paramName);
      if (error) throw error;
      const values: IndexationValues = {};
      for (const row of data || []) {
        values[row.month_key] = Number(row.value);
      }
      return values;
    } catch (error) {
      console.error('Error loading indexation values:', error);
      return {};
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      let overrides: TariffOverride[] = [];
      if (participant?.id) {
        overrides = await fetchTariffOverrides(participant.id);
        setTariffOverrides(overrides);
      }

      const idxPromises: Promise<IndexationValues>[] = [];
      let idxPromise: Promise<IndexationValues> | null = null;
      let seIdxPromise: Promise<IndexationValues> | null = null;

      if (participant?.contract_formula_type === 'variable' && participant?.formula_x_param) {
        idxPromise = loadIndexationValues(participant.formula_x_param);
      }
      if (participant?.shared_energy_formula_type === 'variable' && participant?.shared_energy_x_param) {
        seIdxPromise = loadIndexationValues(participant.shared_energy_x_param);
      }

      const [idxValues, seIdxValues] = await Promise.all([
        idxPromise || Promise.resolve({}),
        seIdxPromise || Promise.resolve({})
      ]);

      setIndexationValues(idxValues);
      setSharedEnergyIdxValues(seIdxValues);

      let splits: Record<string, MonthSplit> = {};
      if (participant?.tariff_type === 'bi' && participant?.ean_code) {
        try {
          const { data: csvLinks, error: csvError } = await supabase
            .from('monthly_csv_links')
            .select('csv_url')
            .eq('year', Number(selectedYear));
          if (!csvError && csvLinks) {
            const urls = [...new Set(csvLinks.map((l: any) => l.csv_url).filter(Boolean))];
            const volumeType = participant.type === 'producer' ? 'Injection Partagée' : 'Consommation Partagée';
            splits = await loadParticipantVolumeSplit(participant.ean_code, Number(selectedYear), urls, volumeType);
          }
        } catch (e) {
          console.error('Erreur chargement volumes semaine/week-end:', e);
        }
      }
      setVolumeSplits(splits);

      loadMonthlyData(idxValues, seIdxValues, splits);
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const calculateSupplierTariff = (
    monthKey: string,
    idxValues: IndexationValues,
    isHP: boolean = true
  ): number => {
    if (participant?.contract_formula_type === 'fixed') {
      if (participant.tariff_type === 'bi' && isHP) {
        return participant.commodity_rate_hp || participant.commodity_rate || DEFAULT_REFERENCE_TARIFF;
      }
      if (participant.tariff_type === 'bi' && !isHP) {
        return participant.commodity_rate_hc || participant.commodity_rate || DEFAULT_REFERENCE_TARIFF;
      }
      return participant.commodity_rate || DEFAULT_REFERENCE_TARIFF;
    }

    if (participant?.contract_formula_type === 'variable') {
      const xValue = idxValues[monthKey];
      if (xValue === undefined) {
        return DEFAULT_REFERENCE_TARIFF;
      }

      const c = parseFloat(participant.formula_c) || 0;

      if (participant.tariff_type === 'mono') {
        const a = parseFloat(participant.formula_a) || 0;
        const b = parseFloat(participant.formula_b) || 0;
        return a * xValue + b + c;
      } else {
        if (isHP) {
          const a1 = parseFloat(participant.formula_a1) || 0;
          const b1 = parseFloat(participant.formula_b1) || 0;
          return a1 * xValue + b1 + c;
        } else {
          const a2 = parseFloat(participant.formula_a2) || 0;
          const b2 = parseFloat(participant.formula_b2) || 0;
          return a2 * xValue + b2 + c;
        }
      }
    }

    return DEFAULT_REFERENCE_TARIFF;
  };

  const calculateSharedEnergyPrice = (monthKey: string, seIdxVals: IndexationValues): number => {
    if (participant?.shared_energy_formula_type === 'variable' && participant?.shared_energy_a && participant?.shared_energy_x_param) {
      const a = parseFloat(participant.shared_energy_a) || 0;
      const xVal = seIdxVals[monthKey];
      if (xVal !== undefined) return a * xVal;
    }
    const overrides = tariffOverrides.filter(o => o.price_type === 'shared_energy_price');
    return resolvePriceForMonth(participant?.shared_energy_price ?? null, overrides, monthKey);
  };

  const loadMonthlyData = (idxValues: IndexationValues, seIdxVals: IndexationValues = {}, splits: Record<string, MonthSplit> = {}) => {
    if (!participant?.monthly_data) {
      setMonthlyData([]);
      return;
    }

    let data;
    try {
      data = typeof participant.monthly_data === 'string'
        ? JSON.parse(participant.monthly_data)
        : participant.monthly_data;
    } catch {
      setMonthlyData([]);
      return;
    }

    const yearMonths = Object.keys(data)
      .filter((m) => m.startsWith(selectedYear))
      .sort();
    const lastMonth = yearMonths[yearMonths.length - 1];

    let hasZeroTransportElia = false;
    if (lastMonth && participant?.billing_data) {
      const billing = typeof participant.billing_data === 'string'
        ? JSON.parse(participant.billing_data)
        : participant.billing_data;
      const lastBilling = billing[lastMonth];
      const transportElia = lastBilling?.networkCosts?.transportELIA;
      hasZeroTransportElia = transportElia !== undefined && Number(transportElia) === 0;
    }

    const monthlyArray = Object.entries(data)
      .filter(([month]) => month.startsWith(selectedYear))
      .map(([month, values]: [string, any]) => {
        const volumePartage = Number(values.volume_partage || 0);
        const volumeComplementaire = Number(values.volume_complementaire || 0);
        const injectionPartagee = Number(values.injection_partagee || 0);
        const injectionComplementaire = Number(values.injection_complementaire || 0);

        const totalConso = volumePartage + volumeComplementaire;
        const totalInjection = injectionPartagee + injectionComplementaire;

        const isProducer = participant.type === 'producer';
        const sharedPercent = isProducer
          ? (totalInjection > 0 ? (injectionPartagee / totalInjection) * 100 : 0)
          : (totalConso > 0 ? (volumePartage / totalConso) * 100 : 0);

        const sharedMWh = isProducer
          ? injectionPartagee / 1000
          : volumePartage / 1000;

        const sharedEnergyPrice = calculateSharedEnergyPrice(month, seIdxVals);

        let savings = 0;
        let supplierTariff = DEFAULT_REFERENCE_TARIFF;
        let supplierTariffHP = DEFAULT_REFERENCE_TARIFF;
        let supplierTariffHC = DEFAULT_REFERENCE_TARIFF;

        const networkSavings = hasZeroTransportElia ? sharedMWh * NETWORK_SAVINGS_RATE : 0;

        if ((participant?.contract_formula_type === 'variable' || participant?.contract_formula_type === 'fixed') && participant?.tariff_type === 'bi') {
          supplierTariffHP = calculateSupplierTariff(month, idxValues, true);
          supplierTariffHC = calculateSupplierTariff(month, idxValues, false);

          const split = splits[month];
          if (split && split.total > 0) {
            const weekdayMWh = split.weekday / 1000;
            const weekendMWh = split.weekend / 1000;
            savings = weekdayMWh * (supplierTariffHP - sharedEnergyPrice)
                    + weekendMWh * (supplierTariffHC - sharedEnergyPrice);
          } else {
            const hpWeight = 5 / 7;
            const hcWeight = 2 / 7;
            const weightedTariff = supplierTariffHP * hpWeight + supplierTariffHC * hcWeight;
            savings = sharedMWh * (weightedTariff - sharedEnergyPrice);
          }
          supplierTariff = supplierTariffHP;
        } else {
          supplierTariff = calculateSupplierTariff(month, idxValues, true);
          supplierTariffHP = supplierTariff;
          supplierTariffHC = supplierTariff;
          savings = sharedMWh * (supplierTariff - sharedEnergyPrice);
        }

        return {
          month: format(new Date(month + '-01'), 'MMMM', { locale: fr }),
          monthKey: month,
          volume_partage: volumePartage,
          volume_complementaire: volumeComplementaire,
          injection_partagee: injectionPartagee,
          injection_complementaire: injectionComplementaire,
          shared_percent: sharedPercent,
          savings: savings,
          networkSavings: networkSavings,
          sharedEnergyPrice: sharedEnergyPrice,
          supplierTariff: supplierTariff,
          supplierTariffHP: supplierTariffHP,
          supplierTariffHC: supplierTariffHC,
          indexationValue: idxValues[month],
          sharedEnergyIdxValue: seIdxVals[month],
          weekdayVolume: splits[month]?.weekday ?? null,
          weekendVolume: splits[month]?.weekend ?? null,
        };
      })
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    setMonthlyData(monthlyArray);
  };

  const totals = useMemo(() => {
    const result = monthlyData.reduce((acc, m) => ({
      volume_partage: acc.volume_partage + m.volume_partage,
      volume_complementaire: acc.volume_complementaire + m.volume_complementaire,
      injection_partagee: acc.injection_partagee + m.injection_partagee,
      injection_complementaire: acc.injection_complementaire + m.injection_complementaire,
      savings: acc.savings + m.savings,
      networkSavings: acc.networkSavings + (m.networkSavings || 0),
    }), {
      volume_partage: 0,
      volume_complementaire: 0,
      injection_partagee: 0,
      injection_complementaire: 0,
      savings: 0,
      networkSavings: 0,
    });

    const isProducer = participant?.type === 'producer';
    const totalShared = isProducer ? result.injection_partagee : result.volume_partage;
    const totalNetwork = isProducer ? result.injection_complementaire : result.volume_complementaire;
    const total = totalShared + totalNetwork;

    return {
      ...result,
      avgPercent: total > 0 ? (totalShared / total) * 100 : 0
    };
  }, [monthlyData, participant]);

  const getTariffDescription = () => {
    if (participant?.contract_formula_type === 'fixed') {
      if (participant.tariff_type === 'bi') {
        return `Tarif fixe bihoraire - HP: ${participant.commodity_rate_hp || participant.commodity_rate || DEFAULT_REFERENCE_TARIFF} EUR/MWh | HC: ${participant.commodity_rate_hc || participant.commodity_rate || DEFAULT_REFERENCE_TARIFF} EUR/MWh`;
      }
      return `Tarif fixe: ${participant.commodity_rate || DEFAULT_REFERENCE_TARIFF} EUR/MWh`;
    }
    if (participant?.contract_formula_type === 'variable') {
      const param = participant.formula_x_param || 'X';
      if (participant.tariff_type === 'mono') {
        const a = participant.formula_a || 0;
        const b = participant.formula_b || 0;
        const c = participant.formula_c || 0;
        return `Tarif variable: ${a}*${param} + ${b} + ${c}`;
      } else {
        const a1 = participant.formula_a1 || 0;
        const b1 = participant.formula_b1 || 0;
        const a2 = participant.formula_a2 || 0;
        const b2 = participant.formula_b2 || 0;
        const c = participant.formula_c || 0;
        return `Tarif variable bihoraire - HP: ${a1}*${param}+${b1}+${c} | HC: ${a2}*${param}+${b2}+${c}`;
      }
    }
    return `Tarif par défaut: ${DEFAULT_REFERENCE_TARIFF} EUR/MWh (aucune info de prix)`;
  };

  const handleExportPDF = async () => {
    setExporting(true);
    const toastId = toast.loading('Préparation du document...');

    let buttonsToHide: NodeListOf<Element> | null = null;

    try {
      const reportElement = document.getElementById('report-modal-content');

      if (!reportElement) {
        toast.error('Contenu du rapport introuvable', { id: toastId });
        setExporting(false);
        return;
      }

      buttonsToHide = reportElement.querySelectorAll('.no-print');
      buttonsToHide.forEach((btn) => {
        (btn as HTMLElement).style.visibility = 'hidden';
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowHeight: reportElement.scrollHeight,
        height: reportElement.scrollHeight
      });

      if (buttonsToHide) {
        buttonsToHide.forEach((btn) => {
          (btn as HTMLElement).style.visibility = 'visible';
        });
      }

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const margin = 5;

      const pdfWidth = 210;
      const usableWidth = pdfWidth - (margin * 2);
      const ratio = usableWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      const pdfHeight = scaledHeight + (margin * 2);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        compress: true
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, scaledHeight);

      const cleanName = normalizeFileName(participant.name);
      const fileName = `rapport_${cleanName}_${selectedYear}.pdf`;
      pdf.save(fileName);

      toast.success('Rapport PDF exporté avec succès', { id: toastId });
    } catch (error: any) {
      console.error('Erreur export PDF:', error);
      toast.error(`Erreur lors de l'export: ${error.message}`, { id: toastId });
      if (buttonsToHide) {
        buttonsToHide.forEach((btn) => {
          (btn as HTMLElement).style.visibility = 'visible';
        });
      }
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  const isProducer = participant?.type === 'producer';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div id="report-modal-content" className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isProducer ? 'bg-amber-100' : 'bg-teal-100'
                }`}>
                  {isProducer ? <Sun className="w-5 h-5 text-amber-600" /> : <Building2 className="w-5 h-5 text-teal-600" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-neutral-900">Rapport - {participant?.name}</h2>
                  <p className="text-sm text-neutral-500">
                    {isProducer ? 'Producteur' : 'Consommateur'} | EAN: {participant?.ean_code}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="md:hidden p-2 hover:bg-neutral-100 rounded-lg no-print">
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>
            <div className="flex items-center space-x-3 no-print">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
              <button
                onClick={handleExportPDF}
                disabled={exporting || loading}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg disabled:opacity-50 transition-all duration-300"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5A935'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8BD3D'}
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Export...' : 'Exporter PDF'}
              </button>
              <button onClick={onClose} className="hidden md:block p-2 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>
          </div>
        </div>

        <div id="report-content" className="p-6 space-y-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Zap className="w-5 h-5 text-teal-600" />
                    <span className="text-sm font-medium text-teal-700">
                      {isProducer ? 'Injection Partagée' : 'Conso. Partagée'}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-teal-900">
                    {((isProducer ? totals.injection_partagee : totals.volume_partage) / 1000).toFixed(2)} MWh
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">
                      {isProducer ? 'Injection Réseau' : 'Conso. Réseau'}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {((isProducer ? totals.injection_complementaire : totals.volume_complementaire) / 1000).toFixed(2)} MWh
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">% partagé moyen</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{totals.avgPercent.toFixed(1)}%</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Euro className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Économies</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{(totals.savings + totals.networkSavings).toFixed(2)} €</p>
                  {totals.networkSavings > 0 && (
                    <p className="text-xs text-blue-600 mt-1">dont {totals.networkSavings.toFixed(2)} € frais réseaux</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-teal-600" />
                  Evolution mensuelle {selectedYear}
                </h3>
                {monthlyData.length > 0 ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#6B7280" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid #e5e7eb'
                          }}
                          formatter={(value: number, name: string) => [`${value.toLocaleString()} kWh`, name]}
                        />
                        <Legend />
                        {isProducer ? (
                          <>
                            <Bar dataKey="injection_partagee" fill="#F59E0B" name="Injection Partagée" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="injection_complementaire" fill="#8B5CF6" name="Injection Réseau" radius={[2, 2, 0, 0]} />
                          </>
                        ) : (
                          <>
                            <Bar dataKey="volume_partage" fill="#10B981" name="Conso. Partagée" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="volume_complementaire" fill="#3B82F6" name="Conso. Réseau" radius={[2, 2, 0, 0]} />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    Aucune donnée disponible pour {selectedYear}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
                  Pourcentage de votre consommation totale fournie par Sun Is Up
                </h3>
                {monthlyData.length > 0 ? (
                  <>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 100]} stroke="#6B7280" tick={{ fontSize: 11 }} unit="%" />
                          <Tooltip
                            formatter={(value: number) => [`${value.toFixed(1)}%`, '% partagé']}
                            contentStyle={{
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              border: '1px solid #e5e7eb'
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="shared_percent"
                            stroke="#F59E0B"
                            strokeWidth={3}
                            dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                            name="% partagé"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-neutral-200">
                            <th className="text-left py-2 px-3 font-medium text-neutral-600">Mois</th>
                            <th className="text-right py-2 px-3 font-medium text-neutral-600">% partagé</th>
                            {participant?.contract_formula_type !== 'fixed' || participant?.commodity_rate || participant?.commodity_rate_hp ? (
                              <th className="text-right py-2 px-3 font-medium text-neutral-600">Tarif fournisseur (€/MWh)</th>
                            ) : null}
                            {(
                              <th className="text-right py-2 px-3 font-medium text-neutral-600">Prix Sun Is Up (€/MWh)</th>
                            )}
                            <th className="text-right py-2 px-3 font-medium text-neutral-600">Économies</th>
                            {totals.networkSavings > 0 && (
                              <th className="text-right py-2 px-3 font-medium text-neutral-600">Écon. frais réseaux</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyData.map((m, i) => (
                            <tr key={i} className="border-b border-neutral-100">
                              <td className="py-2 px-3 text-neutral-900">{m.month}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`font-medium ${m.shared_percent >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                                  {m.shared_percent.toFixed(1)}%
                                </span>
                              </td>
                              {participant?.contract_formula_type !== 'fixed' || participant?.commodity_rate || participant?.commodity_rate_hp ? (
                                <td className="py-2 px-3 text-right text-neutral-600">
                                  {participant?.tariff_type === 'bi' ? (
                                    <span className="text-xs">
                                      <span className="text-blue-600">{m.supplierTariffHP?.toFixed(1)}</span>
                                      {' / '}
                                      <span className="text-emerald-600">{m.supplierTariffHC?.toFixed(1)}</span>
                                    </span>
                                  ) : (
                                    `${m.supplierTariff?.toFixed(1)}`
                                  )}
                                </td>
                              ) : null}
                              <td className="py-2 px-3 text-right text-amber-600 font-medium">
                                {tariffOverrides.filter(o => o.price_type === 'shared_energy_price').length > 0
                                ? `${participant?.shared_energy_price || SHARED_ENERGY_PRICE} → ${resolvePriceForMonth(participant?.shared_energy_price ?? null, tariffOverrides.filter(o => o.price_type === 'shared_energy_price'), `${selectedYear}-01`)} (variable)`
                                : (participant?.shared_energy_price || SHARED_ENERGY_PRICE)}
                              </td>
                              <td className="py-2 px-3 text-right font-medium text-green-600">
                                {m.savings.toFixed(2)} €
                              </td>
                              {totals.networkSavings > 0 && (
                                <td className="py-2 px-3 text-right font-medium text-blue-600">
                                  {(m.networkSavings || 0).toFixed(2)} €
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-neutral-50 font-semibold">
                            <td className="py-2 px-3 text-neutral-900">Total - {selectedYear}</td>
                            <td className="py-2 px-3 text-right text-amber-600">{totals.avgPercent.toFixed(1)}%</td>
                            {(participant?.contract_formula_type === 'variable' || participant?.commodity_rate || participant?.commodity_rate_hp) && (
                              <>
                                <td className="py-2 px-3 text-right">-</td>
                                <td className="py-2 px-3 text-right">-</td>
                              </>
                            )}
                            <td className="py-2 px-3 text-right text-green-600">{totals.savings.toFixed(2)} €</td>
                            {totals.networkSavings > 0 && (
                              <td className="py-2 px-3 text-right text-blue-600">{totals.networkSavings.toFixed(2)} €</td>
                            )}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    Aucune donnée disponible pour {selectedYear}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                  <Euro className="w-5 h-5 mr-2 text-green-600" />
                  Détail du calcul des économies mensuelles
                </h3>
                <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-neutral-600">
                    <strong>Méthode de calcul:</strong> (Tarif fournisseur - Prix énergie partagée) x {isProducer ? 'Injection' : 'Consommation'} partagée
                  </p>
                  {participant?.tariff_type === 'bi' && (
                    <p className="text-sm text-neutral-600 mt-1">
                      <strong>Contrat bihoraire:</strong> la consommation partagée en semaine (lundi-vendredi) est comparée au tarif heure pleine, celle du week-end au tarif heure creuse.
                    </p>
                  )}
                  <p className="text-sm text-neutral-500 mt-1">
                    {getTariffDescription()}
                  </p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Prix énergie partagée: {resolvePriceForMonth(participant?.shared_energy_price ?? null, tariffOverrides.filter(o => o.price_type === 'shared_energy_price'), `${selectedYear}-01`)} EUR/MWh{tariffOverrides.filter(o => o.price_type === 'shared_energy_price').length > 0 ? ' (avec modifications)' : ''}
                  </p>
                  {participant?.contract_formula_type === 'variable' && (
                    <p className="text-xs text-teal-600 mt-2">
                      Le tarif fournisseur est calculé chaque mois en fonction de la valeur de {participant.formula_x_param}
                    </p>
                  )}
                  {participant?.tariff_type === 'bi' && (
                    <p className="text-xs text-blue-600 mt-1">
                      Tarif bihoraire: HP (semaine 7h-22h) et HC (nuit + week-end). Économies calculées sur la répartition semaine/week-end des volumes partagés.
                    </p>
                  )}
                  {totals.networkSavings > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      Économies frais réseaux: {NETWORK_SAVINGS_RATE} €/MWh partagé supplémentaires (transport ELIA à zéro sur le dernier mois de données).
                    </p>
                  )}
                </div>
                {monthlyData.length > 0 ? (
                  <div className="h-80 md:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#6B7280" tick={{ fontSize: 11 }} unit=" €" />
                        <Tooltip
                          formatter={(value: number) => [`${value.toFixed(2)} €`, 'Économies']}
                          contentStyle={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid #e5e7eb'
                          }}
                        />
                        <Bar dataKey="savings" fill="#10B981" name="Économies" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500">
                    Aucune donnée disponible pour {selectedYear}
                  </div>
                )}
              </div>

              <div className="bg-neutral-50 rounded-xl p-4 text-xs text-neutral-500">
                <p>
                  <strong>Note:</strong> {getTariffDescription()}.
                  Prix énergie partagée: {resolvePriceForMonth(participant?.shared_energy_price ?? null, tariffOverrides.filter(o => o.price_type === 'shared_energy_price'), `${selectedYear}-01`)} EUR/MWh{tariffOverrides.filter(o => o.price_type === 'shared_energy_price').length > 0 ? ' (avec modifications de tarif)' : ''}.
                  Ces valeurs sont indicatives.
                </p>
                <p className="mt-1">
                  Rapport généré le {format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })} pour {participant?.name}
                  {participant?.entry_date && ` (membre depuis le ${format(new Date(participant.entry_date), 'dd/MM/yyyy', { locale: fr })})`}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export async function generateReportPDF(
  participant: any,
  year: string
): Promise<Blob | null> {
  return new Promise(async (resolve) => {
    try {
      const React = await import('react');
      const ReactDOM = await import('react-dom/client');
      const html2canvasMod = await import('html2canvas');
      const html2canvas = html2canvasMod.default;
      const jsPDFMod = await import('jspdf');
      const jsPDF = jsPDFMod.default;

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-99999px';
      tempContainer.style.top = '0';
      tempContainer.style.zIndex = '-1';
      tempContainer.style.width = '1200px';
      tempContainer.id = `temp-report-${Date.now()}`;
      document.body.appendChild(tempContainer);

      const cleanup = () => {
        setTimeout(() => {
          if (tempContainer.parentNode) {
            document.body.removeChild(tempContainer);
          }
        }, 100);
      };

      const ReportWrapper = () => {
        const [isOpen, setIsOpen] = React.useState(true);

        React.useEffect(() => {
          if (!isOpen) return;

          const waitForContent = async () => {
            const maxAttempts = 20;
            let attempts = 0;

            while (attempts < maxAttempts) {
              const reportElement = tempContainer.querySelector('#report-modal-content');
              if (!reportElement) {
                await new Promise(r => setTimeout(r, 200));
                attempts++;
                continue;
              }

              const hasCharts = reportElement.querySelectorAll('.recharts-wrapper').length > 0;
              const hasContent = (reportElement as HTMLElement).scrollHeight > 500;

              if (hasCharts && hasContent) {
                return reportElement;
              }

              await new Promise(r => setTimeout(r, 200));
              attempts++;
            }

            return tempContainer.querySelector('#report-modal-content');
          };

          const generatePDF = async () => {
            try {
              const reportElement = await waitForContent();
              if (!reportElement) {
                resolve(null);
                cleanup();
                return;
              }

              await new Promise(r => setTimeout(r, 200));

              const buttonsToHide = reportElement.querySelectorAll('.no-print');
              buttonsToHide.forEach((btn) => {
                (btn as HTMLElement).style.visibility = 'hidden';
              });

              const canvas = await html2canvas(reportElement as HTMLElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowHeight: (reportElement as HTMLElement).scrollHeight,
                height: (reportElement as HTMLElement).scrollHeight
              });

              buttonsToHide.forEach((btn) => {
                (btn as HTMLElement).style.visibility = 'visible';
              });

              const imgWidth = canvas.width;
              const imgHeight = canvas.height;
              const margin = 5;
              const pdfWidth = 210;
              const usableWidth = pdfWidth - (margin * 2);
              const ratio = usableWidth / imgWidth;
              const scaledHeight = imgHeight * ratio;
              const pdfHeight = scaledHeight + (margin * 2);

              const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [pdfWidth, pdfHeight],
                compress: true
              });

              const imgData = canvas.toDataURL('image/jpeg', 0.92);
              pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, scaledHeight);

              const blob = pdf.output('blob');
              resolve(blob);
              cleanup();
            } catch (error) {
              console.error('Error generating report PDF:', error);
              resolve(null);
              cleanup();
            }
          };

          generatePDF();
        }, [isOpen]);

        return React.createElement(ParticipantReportModal, {
          isOpen,
          onClose: () => setIsOpen(false),
          participant,
          initialYear: year
        });
      };

      const root = ReactDOM.createRoot(tempContainer);
      root.render(React.createElement(ReportWrapper));

    } catch (error) {
      console.error('Error in generateReportPDF:', error);
      resolve(null);
    }
  });
}
